'use client'

import type { Options } from 'highcharts'

import { AdaptiveCurveIrmLib } from '@morpho-org/blue-sdk'
import { formatters } from '@morpho-org/ui-v6/utils'
import { memo, useMemo } from 'react'
import { formatUnits } from 'viem'

import type { ChartBaseProps } from '../types/chart'

import {
  createPlotlineLabelFormatter,
  createTooltipFormatter,
  formatSeriesData,
  mergeOptions
} from '../helpers/highcharts'
import { useChartTheme } from '../hooks/useChartTheme'
import { useHighchartsOptions } from '../hooks/useHighchartsOptions'
import { Chart } from './Chart'

export interface IRMData {
  borrowApy: number
  supplyApy: number
  utilization: number
}

export interface InterestRateModelChartProps extends Omit<ChartBaseProps, 'unit' | 'series'> {
  irmAddress?: string
  utilization: number
  data?: IRMData[] | null
}

const TARGET_UTILIZATION = Number(formatUnits(AdaptiveCurveIrmLib.TARGET_UTILIZATION, 18))

export function InterestRateModelChart({
  statuses,
  height,
  irmAddress,
  utilization,
  data
}: InterestRateModelChartProps) {
  const { theme, themeMode } = useChartTheme()

  const { options: baseOptions } = useHighchartsOptions()

  const seriesData = useMemo(() => {
    if (!irmAddress || !data) {
      return {
        borrowApyCurve: [],
        supplyApyCurve: []
      }
    }

    // come from V1
    // if (
    //   !SUPPORTED_INTEREST_RATE_MODELS.some(
    //     (a) => safeGetAddress(a) === safeGetAddress(irmAddress)
    //   )
    // ) {
    //   return {
    //     borrowApyCurve: [],
    //     supplyApyCurve: [],
    //   };
    // }

    const borrowApyCurve = formatSeriesData(
      data.map(({ utilization, borrowApy }) => [utilization, borrowApy])
    )
    const supplyApyCurve = formatSeriesData(
      data.map(({ utilization, supplyApy }) => [utilization, supplyApy])
    )

    return {
      borrowApyCurve,
      supplyApyCurve
    }
  }, [data, irmAddress])

  const formattedTargetUtilization = formatters.percent
    .digits(0)
    .unit('%')
    .trailingZero(false)
    .of(TARGET_UTILIZATION)

  const formattedCurrentUtilization = formatters.percent
    .digits(2)
    .unit('%')
    .trailingZero(false)
    .of(utilization)

  const options: Options = useMemo(() => {
    return mergeOptions(baseOptions, {
      chart: {
        plotBackgroundColor: 'var(--muted)'
      },
      plotOptions: {
        series: {
          lineWidth: 1,
          states: {
            hover: {
              lineWidthPlus: 0,
              halo: { size: 0 }
            }
          }
        },
        areaspline: {
          lineWidth: 1
        },
        area: {
          lineWidth: 1
        },
        spline: {
          lineWidth: 1
        }
      },
      yAxis: {
        labels: {
          formatter: function () {
            return formatters.percent.digits(1).unit('%').trailingZero(false).of(Number(this.value))
          },
          style: {
            textAlign: 'right',
            color: 'var(--secondary-foreground)',
            whiteSpace: 'nowrap',
            textOverflow: 'none',
            fontFamily: 'FK Grotesk, sans-serif',
            fontSize: '11px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: 'normal'
          }
        }
      },
      xAxis: {
        min: 0,
        max: 1,
        allowDecimals: true,
        showLastLabel: false,
        labels: {
          formatter: function () {
            return formatters.percent.digits(0).unit('%').trailingZero(false).of(Number(this.value))
          },
          style: {
            color: 'var(--secondary-foreground)',
            whiteSpace: 'nowrap',
            textOverflow: 'none',
            fontFamily: 'FK Grotesk, sans-serif',
            fontSize: '11px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: 'normal'
          }
        },
        plotLines: [
          {
            value: TARGET_UTILIZATION,
            width: 0,
            label: {
              rotation: 0,
              y: 20,
              x: -1,
              text: '90%',
              align: 'center',
              verticalAlign: 'bottom',
              style: {
                color: 'var(--secondary-foreground)',
                whiteSpace: 'nowrap',
                textOverflow: 'none',
                fontFamily: 'FK Grotesk, sans-serif',
                fontSize: '11px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: 'normal'
              }
            }
          },
          {
            value: TARGET_UTILIZATION,
            className: 'hc-plotline-primary',
            color: 'var(--foreground)',
            dashStyle: 'Dot',
            width: 1,
            label: {
              rotation: 0,
              x: 25,
              y: 100,
              useHTML: true,
              text: `Target (${formattedTargetUtilization})`,
              align: 'right',
              formatter: createPlotlineLabelFormatter()
            }
          },
          {
            value: utilization,
            className: 'hc-plotline-valid',
            color: 'var(--success-foreground)',
            dashStyle: 'Dot',
            width: 1,
            label: {
              rotation: 0,
              x: 30,
              y: 60,
              align: 'right',
              useHTML: true,
              text: utilization ? `Current (${formattedCurrentUtilization})` : undefined,
              formatter: createPlotlineLabelFormatter('var(--success-foreground)')
            }
          }
        ]
      },
      tooltip: {
        style: {
          zIndex: theme.zIndex.overlay.mid
        },
        formatter: createTooltipFormatter({
          formatters: {
            x: point => `Utilization: ${formatters.percent.digits(1).unit('%').of(point.x)}`,
            y: point =>
              `${point.series.name}: ${formatters.percent
                .digits(2)
                .unit('%')
                .default('-')
                .of(point.y)}`
            // No showSeriesColor: charts.css repaints the IRM series --primary /
            // --muted-foreground, so the colorIndex swatch would not match the lines.
          },
          theme,
          themeMode
        })
      },
      series: [
        {
          type: 'line',
          name: 'Rate',
          data: seriesData.borrowApyCurve,
          color: 'var(--primary)'
        },
        {
          type: 'line',
          name: 'Supply Rate',
          data: seriesData.supplyApyCurve,
          color: 'var(--muted-foreground)'
        }
      ]
    })
  }, [
    seriesData,
    baseOptions,
    theme,
    themeMode,
    utilization,
    formattedCurrentUtilization,
    formattedTargetUtilization
  ])

  return <Chart className="hc-chart-irm" options={options} statuses={statuses} height={height} />
}

export default memo(InterestRateModelChart)
